import React from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  FormCheck,
  InputGroup,
  Col,
  Tooltip,
  Row,
  OverlayTrigger,
  Modal,
} from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { Prompt } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { validate, ValidationError } from 'class-validator';
import moment from 'moment';
import {
  Bloodwork,
  BloodworkRange,
  PanelField,
  PanelRange,
  User,
  Client,
  UserProfile,
  WhiteBloodCellDifferentialUnit,
  WhiteBloodCellDifferentialUnitType,
  MenstrualPhase,
  I3_SECTIONS,
  CustomMarker,
  definitionsForDefaults,
  gatherCustomMarkersAcrossTests,
  bySectionOrder,
  groupBySectionValue,
  practitionerSectionOptionValue,
  sectionGroupKey,
  sectionOrderAfterRename,
  learnDefinitionsFromMarkers,
  CustomMarkerDefinition,
  CUSTOM_MARKER_UPGRADE_MODAL,
} from '../../models';
import { v4 as uuidv4 } from 'uuid';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { MathUtils } from '../../utils/MathUtils';
import { profileSaveErrorMessage } from '../../utils/profileSaveError';
import Select from 'react-select';
import { GenericModal } from '../common/generic-modal';
import { TimeSavingFeaturesModal } from './time-saving-features-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LABCORP_MVX_PLUS_URL } from '../../models/MetabolicVulnerability';
import { Upload } from './upload';
import { devOnly } from '../../utils/featureVisibility';
import {
  CustomMarkersHeader,
  CustomMarkersTable,
  CustomMarkerRow,
  addDefinitionsToRows,
  carryForwardRowsFrom,
  hasPractitionerInput,
  pickedByHandRows,
  customMarkersFromRows,
  emptyCustomMarkerRow,
  isBlankCustomMarkerRow,
  rowKeysInUse,
  rowsFromCustomMarkers,
  validateCustomMarkerRows,
} from './custom-markers-table';

const Spinner = require('react-spinkit');
// Served from the public/ folder (not bundled) so the app compiles even before
// the image file is added; the diagram lives at public/assets/imgs/menstrual-cycle-phases.jpg
const menstrualCycleDiagram =
  process.env.PUBLIC_URL + '/assets/imgs/menstrual-cycle-phases.jpg';

// The I³ entry card is parked until its design is finished — flip this back to
// true to show it again (it stays env-gated to local dev on top of this flag).
const SHOW_IMMUNE_INFLAMMATORY_CARD = false;

// The MVX Plus entry card is parked until the feature is finished — flip this
// back to true to show it again (it stays env-gated to local dev on top of this
// flag). The MVX report section is unaffected, but with no way to enter values
// it only renders for bloodwork saved before this flag went off.
const SHOW_MVX_CARD = false;

type Props = {
  hideEnterTestResults?: boolean;
  selectedClient?: Client;
  currentUser: User;
  currentUserProfile: UserProfile;
  currentClients: Client[];
  bloodwork?: Bloodwork;
  bloodworkRanges: BloodworkRange[];
  bloodworkEntryStore: BloodworkEntryStore;
  refreshBloodworks: () => void;
  loadClientBloodworks?: (clientId: string) => void;
  updateClient?: (client: Client) => Promise<Client>;
  /**
   * Persist an updated My Markers library. Called after a successful save when
   * the test introduced a custom marker the practitioner has not defined yet.
   */
  learnCustomMarkers?: (library: CustomMarkerDefinition[]) => Promise<any>;
  /**
   * Persist a report section the practitioner named from the Section dropdown,
   * so it is on the list for every client from then on.
   */
  createCustomMarkerSection?: (name: string) => Promise<any> | void;
  /** Correct a section's name in the practitioner's saved list. */
  renameCustomMarkerSection?: (from: string, to: string) => Promise<any> | void;
  history?: any;
  path?: any;
};

type State = {
  [key: string]: any;
  validationErrors: Map<string, string>;
  collectionDate: string;
  collectionDateForUI: Date;
  alerts: Array<any>;
  selectedClient: Client;
  clientId: string;
  disableSelectClient: boolean;
  /**
   * Whether anything has been typed into this form since it was last saved or
   * cleared. Drives the navigation guard only; nothing here is autosaved.
   */
  dirty: boolean;
  showUpgradeModal: boolean;
  showUploadPdfUpgradeModal: boolean;
  showTimeSavingFeaturesModal: boolean;
  hideEnterTestResults: boolean;
  panelTitle: string;
};

function ClientOption(props: any) {
  const { data, isSelected, isFocused, innerRef, innerProps, options } = props;
  const index = (options as any[]).findIndex((o: any) => o.value === data.value);
  let bg = index % 2 === 0 ? '#ffffff' : '#f8f9fa';
  if (isFocused) bg = '#e8f0fe';
  if (isSelected) bg = '#0d6efd';
  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        backgroundColor: bg,
        color: isSelected ? '#ffffff' : '#212529',
        padding: '3px 12px',
        fontSize: '13px',
        borderBottom: '1px solid #eeeeee',
        cursor: 'pointer',
        fontWeight: isSelected ? 600 : 400,
      }}
    >
      {data.label}
    </div>
  );
}

/**
 * Where the pre-filled custom markers came from, phrased for the banner above
 * the table.
 *
 * The client's own history only. Rows that arrived from the sections added to
 * every test are deliberately left unnamed: those sections are already on
 * screen, each with a card of its own carrying its name and its marker count,
 * so naming them again describes what the practitioner is looking at. A row
 * recovered from a previous test has no such card to point at, and that is the
 * one piece of provenance nothing else on the page carries. The banner still
 * appears for a defaulted panel (see the table), carrying the rule about blank
 * rows without the redundant sentence about where they came from.
 */
export function customMarkerSourceLabel(
  sourceDates: string[],
): string | undefined {
  if (!sourceDates.length) {
    return undefined;
  }
  return sourceDates.length === 1
    ? `this client\u2019s test on ${moment(sourceDates[0]).format('MM/DD/YYYY')}`
    : 'this client\u2019s previous tests';
}

export class BloodworkEntry extends React.Component<Props, State> {
  selectedClient: Client = this.props.selectedClient
    ? this.props.selectedClient
    : new Client();
  clientId: string = this.props.selectedClient
    ? this.props.selectedClient.id
    : undefined;

  state: State = {
    validationErrors: new Map<string, string>(),
    collectionDate: undefined,
    collectionDateForUI: undefined,
    alerts: [],
    selectedClient: this.selectedClient,
    clientId: this.clientId,
    disableSelectClient: false,
    wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType.PERCENT,
    dirty: false,
    showUpgradeModal: false,
    showCustomMarkersUpgradeModal: false,
    showUploadPdfUpgradeModal: false,
    showTimeSavingFeaturesModal: false,
    showMenstrualPhaseInfo: false,
    hideEnterTestResults: this.props.hideEnterTestResults,
    panelTitle:
      this.props.bloodwork && this.props.bloodwork.isNew()
        ? 'Add Test Results'
        : 'Edit Test Results',
    bwUnits: this.selectedClient?.units || 'US',
    height: this.selectedClient?.height ?? '',
    weight: this.selectedClient?.weight ?? '',
    heightFt: this.selectedClient?.height != null
      ? String(MathUtils.cmToFtIn(this.selectedClient.height).ft)
      : '',
    heightIn: this.selectedClient?.height != null
      ? String(MathUtils.cmToFtIn(this.selectedClient.height).inches)
      : '',
    // Raw string rows for the "Add Your Own Markers to This Test" table.
    // Converted to Bloodwork.customMarkers in handleSubmit; never written to
    // the record in this form.
    customMarkerRows: [emptyCustomMarkerRow()],
    // Collection date the carried-forward rows came from, for the banner.
    customMarkerCarriedForwardFrom: undefined,
    // Set only by a rename on this sitting; see sectionOrderAfterRename.
    sectionCardOrder: undefined,
  };

  async componentDidMount() {
    console.log('BloodworkEntry....componentDidMount.....................');
    // this.props.bloodworkEntryStore.setCurrentBloodwork(new Bloodwork());
    // Scroll-to-top must run on mount as well as componentWillReceiveProps:
    // when navigating in from the Home page Edit button, the bloodwork prop
    // is already set on the store before the route change, so props don't
    // change on mount and componentWillReceiveProps may never fire if all
    // container data is already cached.
    this.scrollToAddTestResultsTop();
    // Nothing on this form is autosaved, and a whole panel is a long sit. The
    // browser's own warning covers closing the tab and reloading; the <Prompt>
    // in render covers navigating inside the app.
    window.addEventListener('beforeunload', this.warnIfUnsaved);
    // Before any client is chosen: the practitioner's panels are a property of
    // THEM, not of the client, so an empty screen here reads as a broken
    // feature.
    this.loadDefaultCustomMarkers();
    const showTimeSavingFeaturesModal =
      await this.displayTimeSavingFeaturesModal();
    if (showTimeSavingFeaturesModal) {
      this.setState({
        showTimeSavingFeaturesModal,
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.warnIfUnsaved);
  }

  /**
   * The browser's leave-this-page prompt, while there is work on the form.
   *
   * Every modern browser ignores the string and shows its own wording, so the
   * only job here is to set returnValue at all.
   */
  private warnIfUnsaved = (event: BeforeUnloadEvent) => {
    if (!this.state.dirty) {
      return undefined;
    }
    event.preventDefault();
    event.returnValue = '';
    return '';
  };

  componentDidUpdate(previousProps: Props) {
    // MobX may hand the profile over after the first render, and the defaults
    // live on it. Guarded by hasPractitionerInput inside, so a later profile
    // refresh cannot reload rows over work in progress.
    if (previousProps.currentUserProfile !== this.props.currentUserProfile) {
      this.loadDefaultCustomMarkers();
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    const bloodworkData: any = {};
    const { history, hideEnterTestResults } = nextProps;
    let bloodwork = new Bloodwork(nextProps.bloodwork);
    // Only scroll to the top when the record being edited actually changes
    // (a genuine "load this record into the form" navigation event). The
    // container is a MobX observer that re-renders as clients, ranges, the
    // user profile, and further-considerations finish loading; scrolling on
    // every one of those incidental prop updates yanks the user back to the
    // top repeatedly while the page is still settling.
    if (nextProps.bloodwork !== this.props.bloodwork) {
      this.scrollTop();
    }
    this.handleNumberOfTestsLimit();

    // setup needed if we are editing bloodwork
    if (bloodwork && !bloodwork.isNew()) {
      console.log('We have bloodwork and its not new.....');

      // Check to see if we came thru the "add" route
      if (this.isAddMode(history)) {
        for (let key of Object.keys(bloodwork)) {
          bloodworkData[key] = '';
        }
        this.hydratedBloodworkId = undefined;
        this.clientResolvedBloodworkId = undefined;
        this.setState({
          ...bloodworkData,
          hideEnterTestResults: false,
          panelTitle: 'Add Test Results',
          wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType.PERCENT,
          // The blanket `= ''` above would leave the custom-marker table with a
          // string where it expects an array.
          customMarkerRows: [emptyCustomMarkerRow()],
          customMarkerCarriedForwardFrom: undefined,
          sectionCardOrder: undefined,
        });
        return;
      }

      const collectionDateForUI = moment(bloodwork.collectionDate).toDate();

      // Fall back to the client's profile values for height/weight/units
      // when the bloodwork itself doesn't have them. Prefer the live client
      // record over bloodwork.client, since the snapshot saved with the test
      // can predate height/weight being added to the client.
      const liveClient = bloodwork.clientId && nextProps.currentClients
        ? Client.findClient(bloodwork.clientId, nextProps.currentClients)
        : undefined;
      const bwClient = liveClient || bloodwork.client;
      const effectiveHeightCm = (bloodwork.height != null && bloodwork.height !== ('' as any))
        ? Number(bloodwork.height)
        : (bwClient?.height ?? null);
      const effectiveWeightLbs = (bloodwork.weight != null && bloodwork.weight !== ('' as any))
        ? Number(bloodwork.weight)
        : (bwClient?.weight ?? null);
      const effectiveUnits: 'US' | 'SI' = bwClient?.units === 'SI' ? 'SI' : 'US';
      const { ft: effFt, inches: effIn } = effectiveHeightCm != null
        ? MathUtils.cmToFtIn(effectiveHeightCm)
        : { ft: '', inches: '' };

      // This ran unconditionally, and this lifecycle runs on every incidental
      // re-render (the container observes the profile, the ranges, the client
      // list, the considerations and the test, and re-renders as each lands).
      // So opening one saved test re-seeded every field from the record five
      // times over — and once anything HAD been typed, the next store update
      // underneath the form quietly put the saved value back. Correcting a
      // glucose of 88 to 95 and then loading more test results in the list
      // below left 95 on screen for as long as it took the store to answer.
      const recordChanged = this.hydratedBloodworkId !== bloodwork.id;
      // The client list usually lands after the record, and height, weight and
      // units are read off the LIVE client. So they get a second chance, once,
      // when a real client record first becomes available. A record change is a
      // fresh load and always applies; the late top-up only applies while
      // nothing has been typed, so it can never overwrite a height just
      // entered.
      const clientNowResolvable =
        !!liveClient && this.clientResolvedBloodworkId !== bloodwork.id;
      const applyClientFields =
        recordChanged || (clientNowResolvable && !this.state.dirty);

      // Mirrors the store rather than the record, so it follows its prop.
      if (hideEnterTestResults !== this.state.hideEnterTestResults) {
        this.setState({ hideEnterTestResults });
      }

      if (recordChanged) {
        this.hydratedBloodworkId = bloodwork.id;
        // Inside the guard on purpose: it rewrites the differentials in place,
        // and it was running once per re-render on the same record.
        bloodwork.convertWBCDifferentials(bloodwork.wbcDifferentialUnits);
        for (let key of Object.keys(bloodwork)) {
          bloodworkData[key] = bloodwork[key];
        }
        this.setState({
          ...bloodworkData,
          panelTitle: 'Edit Test Results',
          disableSelectClient: true,
          collectionDateForUI: collectionDateForUI,
          customMarkerRows: rowsFromCustomMarkers(bloodwork.customMarkers),
          customMarkerCarriedForwardFrom: undefined,
          sectionCardOrder: undefined,
          // A record just loaded is a form with nothing unsaved on it.
          dirty: false,
        });
      }

      if (applyClientFields) {
        if (liveClient) {
          this.clientResolvedBloodworkId = bloodwork.id;
        }
        this.setState({
          selectedClient: bwClient,
          bwUnits: effectiveUnits,
          height: effectiveHeightCm ?? '',
          weight: effectiveWeightLbs ?? '',
          heightFt: effectiveHeightCm != null ? String(effFt) : '',
          heightIn: effectiveHeightCm != null ? String(effIn) : '',
        });
      }

      // Editing a test that has no custom markers of its own: offer the
      // client's existing ones so they can be back-filled here too. Excludes
      // this record, and carryForwardCustomMarkers bails if anything has been
      // typed, so a test that already has markers is never touched.
      if (
        recordChanged &&
        (!Array.isArray(bloodwork.customMarkers) ||
          bloodwork.customMarkers.length === 0)
      ) {
        this.carryForwardCustomMarkers(
          bloodwork.clientId,
          bloodwork.id,
          bwClient?.gender,
        );
      }
    } else {
      console.log('inside else in componentWillReceiveProps....');
      const preselected = nextProps.selectedClient;
      // Only (re)hydrate the client-derived fields when the container actually
      // hands us a *different real* preselected client — i.e. navigating in
      // from the Clients table "Add Test Results" button (/bloodwork/add/:id).
      // On the plain /bloodwork/add route there is no preselected client, so
      // the container passes a fresh empty `new Client()` on every render. This
      // lifecycle runs on every incidental MobX re-render (clients, ranges,
      // profile, further-considerations finishing loading), so blindly resetting
      // selectedClient here would clobber a client the user just picked from the
      // dropdown — which hides the conditional menstrual-phase field for
      // premenopausal females.
      const preselectedId = preselected?.id;
      const preselectedChanged = preselectedId !== this.props.selectedClient?.id;
      if (preselectedId && preselectedChanged) {
        const heightCm = preselected?.height;
        const clientUnits = preselected?.units || 'US';
        const { ft, inches } = heightCm != null
          ? MathUtils.cmToFtIn(heightCm)
          : { ft: '', inches: '' };
        this.setState({
          hideEnterTestResults,
          panelTitle: 'Add Test Results',
          wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType.PERCENT,
          selectedClient: preselected,
          bwUnits: clientUnits,
          height: heightCm ?? '',
          weight: preselected?.weight ?? '',
          heightFt: heightCm != null ? String(ft) : '',
          heightIn: heightCm != null ? String(inches) : '',
        });
        // Arrived from the Clients table's "Add Test Results" — same carry
        // forward as picking the client from the dropdown.
        this.carryForwardCustomMarkers(
          preselectedId,
          undefined,
          preselected?.gender,
        );
      } else {
        this.setState({
          hideEnterTestResults,
          panelTitle: 'Add Test Results',
          wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType.PERCENT,
        });
      }
    }
  }

  isAddMode(history: any) {
    return (
      history &&
      history.location &&
      history.location.pathname === '/bloodwork/add'
    );
  }

  /**
   * True when the form is editing an existing (already-saved) test rather than
   * adding a new one. Derived from props so it does NOT depend on
   * componentWillReceiveProps having fired — when navigating in from the Home
   * page Edit button the bloodwork prop is already set before the route change,
   * so that lifecycle (and the disableSelectClient state it sets) can be skipped
   * entirely. The client must never be changed on an existing test, so the
   * client dropdown is disabled whenever this is true.
   */
  private isEditingExistingTest(): boolean {
    const { bloodwork, history } = this.props;
    return (
      !!bloodwork &&
      typeof bloodwork.isNew === 'function' &&
      !bloodwork.isNew() &&
      !this.isAddMode(history)
    );
  }

  /**
   * Whether saving this form would add a test the plan does not allow.
   *
   * False while editing a test that already exists: that save replaces a record
   * already counted, so it neither adds to the total nor needs an upgrade.
   */
  private blockedByTestLimit(): boolean {
    const { currentUserProfile } = this.props;
    return (
      !this.isEditingExistingTest() &&
      !!currentUserProfile &&
      !currentUserProfile.canAddTest()
    );
  }

  handleNumberOfTestsLimit() {
    if (this.blockedByTestLimit()) {
      this.setState({ showUpgradeModal: true });
    }
  }

  handleUploadPdfLimit() {
    const { currentUserProfile } = this.props;
    if (currentUserProfile && !currentUserProfile.canUploadPdf()) {
      this.setState({ showUploadPdfUpgradeModal: true });
    }
  }

  handleInputChange = (event: any) => {
    // console.log(event ? event.target : 'event was falsey...');
    if (!this.state.dirty) {
      this.setState({ dirty: true });
    }
    const fieldId = event.target.name;
    let value = event.target.value;

    // Convert empty string to undefined so our automatic calculations work correctly
    if (value.trim().length === 0) {
      value = undefined;
    }

    // If the wbcDifferentialUnits selection changes then clear out wbc differencial inputs
    if (fieldId === 'wbcDifferentialUnits') {
      this.setState({
        [fieldId]: value,
        neutrophils: '',
        lymphocytes: '',
        monocytes: '',
        eosinophils: '',
        basophils: '',
      });
    } else {
      this.setState({
        [fieldId]: value,
      });
    }

    if (fieldId === 'clientId') {
      this.handleClientChange(event.target.value);
    }

    this.clearAlerts();
  };

  handleClientChange = (clientId: string) => {
    const client = Client.findClient(clientId, this.props.currentClients);
    const heightCm = client?.height;
    const clientUnits = client?.units || 'US';
    const { ft, inches } = heightCm != null
      ? MathUtils.cmToFtIn(heightCm)
      : { ft: '', inches: '' };
    this.setState({
      selectedClient: client || new Client(),
      bwUnits: clientUnits,
      height: heightCm ?? '',
      weight: client?.weight ?? '',
      heightFt: heightCm != null ? String(ft) : '',
      heightIn: heightCm != null ? String(inches) : '',
    });
    this.carryForwardCustomMarkers(clientId, undefined, client?.gender);
  };

  /**
   * Pre-fill the custom-marker table with every custom marker this client has
   * ever had, so a repeat panel only needs its values typed rather than each
   * marker defined again.
   *
   * Gathered across the client's WHOLE history, not just their latest test: a
   * marker run in January and skipped in March should still be one keystroke
   * away in June. Markers are keyed by name, and the most recent definition of
   * each wins, so a range the practitioner has since corrected is the one that
   * carries forward.
   *
   * Deliberately silent on failure: this is a convenience, and a client with no
   * prior custom markers (or a lookup that fails) should just leave the table
   * empty rather than surface an error on an unrelated field.
   */
  /**
   * Load the practitioner's default sections and markers, with NO client
   * needed.
   *
   * These used to wait for a client to be picked, because per-sex ranges cannot
   * be resolved without knowing whose test it is. That was the wrong trade: a
   * practitioner opens Add Test Results, sees none of the panels they set up,
   * and writes to support saying the feature is broken. The ranges are resolved
   * again the moment a client IS picked (see carryForwardCustomMarkers), so the
   * only cost of showing them early is that a sex-specific range shows its
   * default until then.
   *
   * Runs on mount and again when the profile arrives, since MobX may deliver it
   * after the first render.
   */
  loadDefaultCustomMarkers = () => {
    const profile = this.props.currentUserProfile;
    if (!profile) {
      return;
    }
    const defaults = definitionsForDefaults(
      profile.customMarkerLibrary,
      profile.defaultTestSections,
      profile.defaultTestMarkerIds,
    );
    if (!defaults.length) {
      return;
    }
    // Never over anything the practitioner has typed, and never a second time
    // over rows this already put there.
    const rows: CustomMarkerRow[] = this.state.customMarkerRows || [];
    if (hasPractitionerInput(rows)) {
      return;
    }
    const next = addDefinitionsToRows(
      rows,
      defaults,
      this.state.selectedClient?.gender,
    );
    if (next === rows) {
      return;
    }
    // No source label: the sections these came from are on screen already as
    // cards of their own. The table still shows the short banner off the rows'
    // own carriedForward flag.
    this.setState({
      customMarkerRows: next,
      customMarkerCarriedForwardFrom: undefined,
    });
  };

  /**
   * The client whose carry-forward is already running.
   *
   * componentWillReceiveProps fires on every incidental MobX re-render (the
   * container observes the profile, the ranges, the client list, the further
   * considerations and the test, and re-renders as each one lands), and on the
   * edit path it calls carryForwardCustomMarkers every time. The cache check
   * inside only helps once a load has come BACK, so opening one saved test
   * fired a full load of the client's entire history five times over, five
   * concurrent round trips each toggling the global busy spinner — which is
   * a boolean, so the first one to finish cleared it while the rest ran on.
   *
   * Keyed by client rather than a bare boolean so genuinely switching client
   * is never the thing that gets swallowed.
   */
  private carryForwardInFlight?: string;

  /**
   * The record already loaded into the form, and the one whose height and
   * weight have been resolved against a LIVE client record.
   *
   * Deliberately NOT a prop-identity comparison. currentBloodwork is set on the
   * store before this screen mounts, so the bloodwork prop is the same object on
   * every one of the re-renders below; gating on identity would mean the form
   * never filled in at all. What matters is whether THIS record has been loaded
   * yet, which is a question about the record, not about the reference.
   */
  private hydratedBloodworkId?: string;
  private clientResolvedBloodworkId?: string;

  carryForwardCustomMarkers = async (
    clientId: string,
    excludeBloodworkId?: string,
    // Passed in rather than read off state: every caller setState()s the newly
    // picked client in the same React batch as this call, and the cached-tests
    // path never awaits, so reading this.state.selectedClient here would
    // resolve per-sex ranges against the PREVIOUS client (or none at all).
    clientGender?: string,
  ) => {
    if (!clientId || this.carryForwardInFlight === clientId) {
      return;
    }
    this.carryForwardInFlight = clientId;
    try {
      await this.runCarryForwardCustomMarkers(
        clientId,
        excludeBloodworkId,
        clientGender,
      );
    } finally {
      this.carryForwardInFlight = undefined;
    }
  };

  private runCarryForwardCustomMarkers = async (
    clientId: string,
    excludeBloodworkId?: string,
    clientGender?: string,
  ) => {
    // Never clobber work in progress — only fill a table the user hasn't
    // typed into. Re-picking a client after typing keeps what they typed.
    //
    // "Anything typed" rather than hasPractitionerInput, because markers picked
    // by hand from My Markers are carried ACROSS the rebuild below rather than
    // blocking it: the practitioner asked for those, and the client's own
    // history should still load around them.
    const rows: CustomMarkerRow[] = this.state.customMarkerRows || [];
    const picked = pickedByHandRows(rows);
    if (rows.some(row => (row.value || '').trim())) {
      return;
    }

    let history: CustomMarkerRow[] = [];
    let sourceDates: string[] = [];

    try {
      const { bloodworkEntryStore } = this.props;
      // The store already holds this client's tests whenever we arrived from
      // their row or are editing one of their tests; only fetch otherwise, so
      // picking a client from the dropdown doesn't flash the loading spinner
      // for nothing.
      const cached = bloodworkEntryStore.clientBloodworks || [];
      const bloodworks =
        cached.length && cached[0].clientId === clientId
          ? cached
          : await bloodworkEntryStore.loadClientBloodworks(clientId);

      // clientBloodworks is ordered newest first, which is what makes the most
      // recent definition of each marker win.
      const gathered = gatherCustomMarkersAcrossTests(bloodworks || [], {
        excludeTestId: excludeBloodworkId,
      });
      history = carryForwardRowsFrom(gathered.markers);
      sourceDates = gathered.sourceDates;
    } catch (error) {
      // Deliberately silent: this is a convenience, and a lookup that fails
      // should still let the practitioner's default sections load rather than
      // surface an error on an unrelated field.
      console.error('Could not carry forward custom markers', error);
    }

    // Guard again: the fetch is async and the user may have started typing.
    const current: CustomMarkerRow[] = this.state.customMarkerRows || [];
    if (current.some(row => (row.value || '').trim())) {
      return;
    }

    // The client's own history first, then the sections set to load onto every
    // test, deduped by marker name. Union rather than either-or: history is
    // what this client actually ran and must never be dropped, and a panel
    // added to the defaults afterwards has to reach an existing client too.
    //
    // Built fresh from `history` rather than from what is on screen, so the
    // rows this client's sex actually calls for replace whatever was resolved
    // before anyone was chosen. Nothing is lost: hasPractitionerInput above has
    // already established that the only rows there are ones we put there.
    const profile = this.props.currentUserProfile;
    const defaults = definitionsForDefaults(
      profile?.customMarkerLibrary,
      profile?.defaultTestSections,
      profile?.defaultTestMarkerIds,
    );
    const gender = clientGender ?? this.state.selectedClient?.gender;
    const rebuilt = addDefinitionsToRows(history, defaults, gender);
    // The picks go on last and keep their flag, so a second client change does
    // not lose them either. addDefinitionsToRows dedupes by name, so one the
    // history or the defaults already brought in is not added twice.
    const library = this.props.currentUserProfile?.customMarkerLibrary || [];
    const pickedDefinitions = picked
      .map(row =>
        library.find(
          definition =>
            CustomMarker.keyFor(definition.name) ===
            CustomMarker.keyFor(row.name),
        ),
      )
      .filter(Boolean) as CustomMarkerDefinition[];
    const next = addDefinitionsToRows(rebuilt, pickedDefinitions, gender, {
      byHand: true,
    });
    if (!next.length) {
      return;
    }

    this.setState({
      customMarkerRows: next,
      customMarkerCarriedForwardFrom: customMarkerSourceLabel(sourceDates),
    });
  };

  /**
   * The practitioner's own sections that have markers on this test, each
   * getting a card of its own above the catch-all.
   *
   * Shipped sections are deliberately excluded: the form already has a card
   * headed "Glucose Regulation & Metabolic Function", and a second one for the
   * two markers filed there would be two cards with one name. Those stay in the
   * catch-all card under their heading, where the heading is the only thing
   * distinguishing them.
   */
  practitionerMarkerSections(): {
    key: string;
    label: string;
    value: string;
    count: number;
  }[] {
    const rows: CustomMarkerRow[] = this.state.customMarkerRows || [];
    const groups = groupBySectionValue(
      rows.map((row, index) => ({ row, index })),
      entry => entry.row.section,
      this.props.currentUserProfile?.customMarkerSections,
    )
      .filter(group => group.kind === 'practitioner')
      .map(group => ({
        key: sectionGroupKey(group.value),
        label: group.label,
        value: group.value,
        count: group.items.length,
      }));
    // Alphabetical, unless a rename on this sitting pinned the order — see
    // sectionOrderAfterRename.
    return bySectionOrder(groups, this.state.sectionCardOrder);
  }

  /**
   * Save a section named from the Section dropdown to the practitioner's
   * profile. The marker keeps the name whatever happens here — it is stored on
   * the test, not looked up — so a failure costs them the section on the NEXT
   * client's dropdown, and that is what the warning says.
   */
  createSection = (name: string) => {
    const created = this.props.createCustomMarkerSection?.(name);
    if (created && typeof created.catch === 'function') {
      created.catch((err: any) => {
        console.error('Failed to save the new section', err);
        this.addAlert(
          {
            type: 'warning',
            text: profileSaveErrorMessage(
              err,
              `“${name}” is set on this marker and saves with the test, but it could not be added to your section list for other clients. You can add it under Test Results, My Markers.`,
            ),
          },
          true,
        );
      });
    }
  };

  /**
   * Correct a section's name in the practitioner's list. The rows on this test
   * are renamed by the table itself; tests already saved keep the name they
   * were saved with until they are opened, which is what stops a rename from
   * retitling a section on a report already given to a client.
   */
  renameSection = (from: string, to: string) => {
    // Read BEFORE the rename lands, so the pin is the order on screen right
    // now: the card the practitioner is working in keeps the slot it has,
    // rather than re-sorting to wherever its new name falls alphabetically.
    const pinned = sectionOrderAfterRename(
      this.practitionerMarkerSections().map(group => group.key),
      sectionGroupKey(practitionerSectionOptionValue(from)),
      sectionGroupKey(practitionerSectionOptionValue(to)),
    );
    if (pinned) {
      this.setState({ sectionCardOrder: pinned });
    }
    const renamed = this.props.renameCustomMarkerSection?.(from, to);
    if (renamed && typeof renamed.catch === 'function') {
      renamed.catch((err: any) => {
        console.error('Failed to rename the section', err);
        this.addAlert(
          {
            type: 'warning',
            text: profileSaveErrorMessage(
              err,
              `The markers on this test now read “${to}”, but your saved section list still says “${from}”. You can rename it under Test Results, My Markers.`,
            ),
          },
          true,
        );
      });
    }
  };

  handleOnBlur = (event: any) => {
    this.handleCalculatedRatioFields(event.target.name);
    this.handleGlobulinCalculation(event.target.name);
    this.handleMeanCorpuscularVolumeCalculation(event.target.name);
    this.handleMeanCorpuscularHGBCalculation(event.target.name);
    this.handleMCHConcentrationCalculation(event.target.name);
    this.handleBilirubinCalculation(event.target.name);
    this.handleSaturationCalculation(event.target.name);
    this.handleTestosteroneBioavailableCalculation(event.target.name);
    this.handleLessThanCalculation(event.target.name);
  };

  // Each ratio gets its own `if`, deliberately not an else-if chain: HDL is a
  // component of BOTH Cholesterol/HDL and Triglycerides/HDL, so a chain stops at
  // the first match and leaves the second ratio stale. That bit TG/HDL — it only
  // repopulated when Triglycerides was blurred, so entering Triglycerides before
  // HDL left it blank. TG/HDL is one of the three primary Glucose markers, so a
  // blank there silently shifts the Glucose gauge and pattern.
  handleCalculatedRatioFields(fieldId: string) {
    if (fieldId === 'bun' || fieldId === 'creatinine') {
      this.handlePopulateRatio('bun', 'creatinine', 'bunCreatinineRatio');
    }
    if (fieldId === 'albumin' || fieldId === 'globulin') {
      this.handlePopulateRatio('albumin', 'globulin', 'albuminGlobulinRatio');
    }
    if (fieldId === 'cholesterol' || fieldId === 'hdl') {
      this.handlePopulateRatio('cholesterol', 'hdl', 'cholesterolHDLRatio');
    }
    if (fieldId === 'triglycerides' || fieldId === 'hdl') {
      this.handlePopulateRatio('triglycerides', 'hdl', 'triglyceridesHDLRatio');
    }
    if (fieldId === 'apolipoproteinB' || fieldId === 'apolipoproteinA1') {
      this.handlePopulateRatio(
        'apolipoproteinB',
        'apolipoproteinA1',
        'apoBapoA1Ratio',
      );
    }
  }

  handlePopulateRatio(
    numerator: string,
    denominator: string,
    ratioField: string,
  ) {
    // Number('') is 0, not NaN. Without an explicit blank check a missing
    // component yields a real-looking ratio — 0 when the numerator is blank
    // (and 0 reads as *optimal* for TG/HDL), or a divide-by-zero when the
    // denominator is. A ratio the lab never reported must stay empty.
    const isMeasured = (raw: any) =>
      raw != null && String(raw).trim() !== '' && !isNaN(Number(raw));

    const rawNumerator = this.state[numerator];
    const rawDenominator = this.state[denominator];

    if (
      !isMeasured(rawNumerator) ||
      !isMeasured(rawDenominator) ||
      Number(rawDenominator) === 0
    ) {
      // Clear rather than leave the previous value behind: a stale ratio
      // computed from the *old* HDL is worse than a visibly empty field.
      this.setState({ [ratioField]: '' });
      return;
    }

    const ratio = MathUtils.round(
      Number(rawNumerator) / Number(rawDenominator),
      2,
    );
    this.setState({ [ratioField]: isNaN(ratio) ? '' : ratio });
  }

  handleGlobulinCalculation(fieldId: string) {
    if (fieldId === 'protein' || fieldId === 'albumin') {
      const protein = Number(this.state.protein);
      const albumin = Number(this.state.albumin);
      if (!isNaN(protein) && !isNaN(albumin)) {
        const globulin = protein - albumin;
        this.setState({
          globulin: MathUtils.round(globulin, 2),
        });
      }
    }
  }

  handleMeanCorpuscularVolumeCalculation(fieldId: string) {
    if (fieldId === 'hematocrit' || fieldId === 'redBloodCells') {
      const hct = Number(this.state.hematocrit);
      const rbc = Number(this.state.redBloodCells);
      if (!isNaN(hct) && !isNaN(rbc)) {
        //  MCV = HCT x 10 / RBC
        const mcv = (hct * 10) / rbc;
        this.setState({
          meanCorpuscularVolume: MathUtils.round(mcv, 2),
        });
      }
    }
  }

  handleMeanCorpuscularHGBCalculation(fieldId: string) {
    if (fieldId === 'hemoglobin' || fieldId === 'redBloodCells') {
      const hgb = Number(this.state.hemoglobin);
      const rbc = Number(this.state.redBloodCells);
      if (!isNaN(hgb) && !isNaN(rbc)) {
        // MCH = HGB x 10 / RBC
        const mch = (hgb * 10) / rbc;
        this.setState({
          meanCorpuscularHGB: MathUtils.round(mch, 2),
        });
      }
    }
  }

  handleMCHConcentrationCalculation(fieldId: string) {
    if (fieldId === 'hemoglobin' || fieldId === 'hematocrit') {
      const hgb = Number(this.state.hemoglobin);
      const hct = Number(this.state.hematocrit);
      if (!isNaN(hgb) && !isNaN(hct)) {
        // MCHC = HGB x 100 / HCT
        const mchc = (hgb * 100) / hct;
        this.setState({
          mchConcentration: MathUtils.round(mchc, 2),
        });
      }
    }
  }

  handleBilirubinCalculation(fieldId: string) {
    // total = direct + indirect
    // direct = total - indirect
    // indirect = total - direct
    if (
      fieldId === 'bilirubin' ||
      fieldId === 'bilirubinDirect' ||
      fieldId === 'bilirubinIndirect'
    ) {
      const total = Number(
        this.state.bilirubin === '' ? undefined : this.state.bilirubin,
      );
      let direct = Number(
        this.state.bilirubinDirect === ''
          ? undefined
          : this.state.bilirubinDirect,
      );
      let indirect = Number(
        this.state.bilirubinIndirect === ''
          ? undefined
          : this.state.bilirubinIndirect,
      );
      if (!isNaN(total) && !isNaN(direct) && isNaN(indirect)) {
        indirect = total - direct;
        this.setState({
          bilirubinIndirect: MathUtils.round(indirect, 2),
        });
      } else if (!isNaN(total) && !isNaN(indirect) && isNaN(direct)) {
        direct = total - indirect;
        this.setState({
          bilirubinDirect: MathUtils.round(direct, 2),
        });
      }
    }
  }

  handleSaturationCalculation(fieldId: string) {
    if (fieldId === 'iron' || fieldId === 'tibc') {
      const iron = Number(this.state.iron);
      const tibc = Number(this.state.tibc);
      if (!isNaN(iron) && !isNaN(tibc)) {
        //  % saturation = ( iron / TIBC ) * 100
        const saturation = (iron / tibc) * 100;
        this.setState({
          saturation: MathUtils.round(saturation, 1),
        });
      }
    }
  }

  handleTestosteroneBioavailableCalculation(fieldId: string) {
    if (
      fieldId === 'testosteroneBioavailable' ||
      fieldId === 'testosteroneTotal'
    ) {
      const bioavailable = Number(this.state.testosteroneBioavailable);
      const total = Number(this.state.testosteroneTotal);
      if (!isNaN(bioavailable) && !isNaN(total)) {
        const bioavailablePercentage = (bioavailable / total) * 100;
        this.setState({
          testosteroneBioavailablePercent: MathUtils.round(
            bioavailablePercentage,
            1,
          ),
        });
      }
    }
  }

  calculateEGFRFromState(): number | null {
    const creatinine = this.state.creatinine != null && this.state.creatinine !== ''
      ? Number(this.state.creatinine) : null;
    const client: Client = this.state.selectedClient;

    if (!creatinine || !client?.dateOfBirth || !client?.gender) return null;

    const collectionDate = this.state.collectionDate;
    if (!collectionDate) return null;

    const age = client.ageAsOf(collectionDate);
    if (!age || age <= 0) return null;

    if (age < 18) {
      const heightCm = (this.state.height != null && this.state.height !== '')
        ? Number(this.state.height)
        : client.height;
      if (!heightCm) return null;
      return Math.round((0.413 * heightCm) / creatinine * 10) / 10;
    }

    // CKD-EPI 2021 (race-free) for adults ≥18
    const isFemale = client.isFemale();
    const kappa = isFemale ? 0.7 : 0.9;
    const alpha = isFemale ? -0.241 : -0.302;
    const sexFactor = isFemale ? 1.012 : 1.0;
    const ratio = creatinine / kappa;
    return Math.round(
      142 *
        Math.pow(Math.min(ratio, 1), alpha) *
        Math.pow(Math.max(ratio, 1), -1.2) *
        Math.pow(0.9938, age) *
        sexFactor *
        10,
    ) / 10;
  }

  handleLessThanCalculation(fieldId: string) {
    if (fieldId === 'tpoAb' && this.state.tpoAb === '<9') {
      this.setState({ tpoAb: 8 });
    }

    if (fieldId === 'tpoAb' && this.state.tpoAb === '<12') {
      this.setState({ tpoAb: 11.9 });
    }

    if (fieldId === 'tpoAb' && this.state.tpoAb === '<28') {
      this.setState({ tpoAb: 27.9 });
    }

    if (fieldId === 'tpoAb' && this.state.tpoAb === '<0.3') {
      this.setState({ tpoAb: 0.2 });
    }

    if (fieldId === 'tpoAb' && this.state.tpoAb === '<1') {
      this.setState({ tpoAb: 0.9 });
    }

    if (fieldId === 'tgAb' && ['<1', '<1.0', '≤1'].includes(this.state.tgAb)) {
      this.setState({ tgAb: 0.9 });
    }

    if (fieldId === 'tgAb' && ['<1.5', '<1.50', '≤1.5'].includes(this.state.tgAb)) {
      this.setState({ tgAb: 1.4 });
    }

    if (fieldId === 'tgAb' && ['<15', '<15.000'].includes(this.state.tgAb)) {
      this.setState({ tgAb: 14.99 });
    }

    if (fieldId === 'tgAb' && ['>2250', '>2250.0'].includes(this.state.tgAb)) {
      this.setState({ tgAb: 2251 });
    }

    if (fieldId === 'estroneE1' && this.state.estroneE1 === '<6') {
      this.setState({ estroneE1: 5.9 });
    }

    if (fieldId === 'estriolE3' && this.state.estriolE3 === '<0.10') {
      this.setState({ estriolE3: 0.09 });
    }

        if (fieldId === 'estriolE3' && this.state.estriolE3 === '<0.2') {
      this.setState({ estriolE3: 0.19 });
    }

    if (fieldId === 'bilirubin' && this.state.bilirubin === '<0.2') {
      this.setState({ bilirubin: 0.19 });
    }

    if (
      fieldId === 'bilirubinDirect' && this.state.bilirubinDirect === '<0.2') {
      this.setState({ bilirubinDirect: 0.19 });
    }

    if (fieldId === 'insulin' && this.state.insulin === '<3') {
      this.setState({ insuln: 2.9 });
    }

    if (
      fieldId === 'testosteroneFreeDirect' && this.state.testosteroneFreeDirect === '<0.2') {
      this.setState({ testosteroneFreeDirect: 0.19 });
    }

    if (
      fieldId === 'testosteroneTotal' && this.state.testosteroneTotal === '<2.5') {
      this.setState({ testosteroneTotal: 2.4 });
    }

    if (fieldId === 'lipoproteinA' && this.state.lipoproteinA === '<8.4') {
      this.setState({ lipoproteinA: 8.3 });
    }

    if (fieldId === 'lipoproteinA' && this.state.lipoproteinA === '<10') {
      this.setState({ lipoproteinA: 9.9 });
    }

    if (fieldId === 'lipoproteinA' && this.state.lipoproteinA === '<19.6') {
      this.setState({ lipoproteinA: 19.5 });
    }

    if (fieldId === 'folateSerum' && this.state.folateSerum === '>20.0') {
      this.setState({ folateSerum: 21 });
    }

    if (fieldId === 'folateSerum' && this.state.folateSerum === '>24.0') {
      this.setState({ folateSerum: 25 });
    }

    if (fieldId === 'folateRBC' && this.state.folateRBC === '>1396') {
      this.setState({ folateRBC: 1397 });
    }

    if (fieldId === 'folateRBC' && this.state.folateRBC === '>1325') {
      this.setState({ folateRBC: 1326 });
    }

    if (fieldId === 'bileAcids' && this.state.bileAcids === '<1.0') {
      this.setState({ bileAcids: 0.9 });
    }

    if (fieldId === 'ldl' && this.state.ldl === '<100') {
      this.setState({ ldl: 99 });
    }

    if (fieldId === 'estradiolE2' && this.state.estradiolE2 === '<15') {
      this.setState({ estradiolE2: 14.9 });
    }

    if (fieldId === 'estradiolE2' && this.state.estradiolE2 === '<10') {
      this.setState({ estradiolE2: 9.9 });
    }

    if (fieldId === 'estradiolE2' && this.state.estradiolE2 === '<5.0') {
      this.setState({ estradiolE2: 4.9 });
    }

    if (fieldId === 'estradiolE2' && this.state.estradiolE2 === '<30') {
      this.setState({ estradiolE2: 29.9 });
    }

    if (fieldId === 'pregnenolone' && this.state.pregnenolone === '<5') {
      this.setState({ pregnenolone: 4 });
    }

    if (fieldId === 'progesterone' && this.state.progesterone === '<0.5') {
      this.setState({ progesterone: 0.4 });
    }

    if (fieldId === 'progesterone' && this.state.progesterone === '<0.1') {
      this.setState({ progesterone: 0.09 });
    }
    
    if (fieldId === 'progesterone' && this.state.progesterone === '<0.05') {
      this.setState({ progesterone: 0.04 });
    }

    if (fieldId === 'lh' && this.state.lh === '<0.05') {
      this.setState({ lh: 0.04 });
    }

    if (fieldId === 'hsCRP' && this.state.hsCRP === '<0.2') {
      this.setState({ hsCRP: 0.1 });
    }

    if (fieldId === 'hsCRP' && this.state.hsCRP === '<0.15') {
      this.setState({ hsCRP: 0.14 });
    }

    if (fieldId === 'eGFR' && (this.state.eGFR === '>90' || this.state.eGFR === '>60')) {
      const fallback = this.state.eGFR === '>90' ? 91 : 61;
      this.setState({ eGFR: this.calculateEGFRFromState() ?? fallback });
    }

    if (fieldId === 'eGFRforAA' && (this.state.eGFRforAA === '>90' || this.state.eGFRforAA === '>60')) {
      const fallback = this.state.eGFRforAA === '>90' ? 91 : 61;
      this.setState({ eGFRforAA: this.calculateEGFRFromState() ?? fallback });
    }

    if (fieldId === 'dDimer' && this.state.dDimer === '<0.20') {
      this.setState({ dDimer: 0.1 });
    }

    if (fieldId === 'psaTotal' && this.state.psaTotal === '<0.01') {
      this.setState({ psaTotal: 0.009 });
    }
    if (fieldId === 'psaFree' && this.state.psaFree === '<0.01') {
      this.setState({ psaFree: 0.009 });
    }

  }

  handleCollectionDateChange = (value: any) => {
    const startOfDay = moment(value).startOf('day').toISOString();
    this.setState({
      collectionDate: startOfDay,
      collectionDateForUI: value,
      dirty: true,
    });
    this.clearAlerts();
  };

  handleSubmit = (event: any) => {
    event.preventDefault();

    // Only a NEW test counts against the allowance. Correcting one that is
    // already saved is not adding one, and this used to refuse it: an
    // over-limit practitioner who opened a saved test to fix a typo got the
    // upgrade popup and watched the correction disappear with it.
    if (this.blockedByTestLimit()) {
      this.handleNumberOfTestsLimit();
      return;
    }

    let validationErrors = new Map<string, string>();
    let bloodwork: Bloodwork = new Bloodwork();
    bloodwork.practitionerId = this.props.currentUser.subject;

    for (let key of Object.keys(this.state)) {
      if (
        key !== 'validationErrors' &&
        key !== 'alerts' &&
        key !== 'selectedClient' &&
        key !== 'collectionDateForUI' &&
        key !== 'hideEnterTestResults' &&
        key !== 'panelTitle' &&
        key !== 'disableSelectClient' &&
        key !== 'showUpgradeModal' &&
        key !== 'showCustomMarkersUpgradeModal' &&
        key !== 'showUploadPdfUpgradeModal' &&
        key !== 'showTimeSavingFeaturesModal' &&
        key !== 'showMenstrualPhaseInfo' &&
        key !== 'heightFt' &&
        key !== 'heightIn' &&
        key !== 'bwUnits' &&
        // Custom markers are a nested array, not a scalar. Left to the generic
        // path below they would be mangled: Number([]) is 0, so a test with no
        // custom markers would save `customMarkers: 0`. Assigned explicitly
        // after this loop instead.
        key !== 'customMarkerRows' &&
        key !== 'customMarkerCarriedForwardFrom' &&
        key !== 'customMarkers' &&
        // Same trap, and this form has no UI for them: the Recommended
        // Supplements edits are made on the analysis screen. Carried across
        // explicitly below so an edit survives a test being re-saved here.
        key !== 'supplementEdits'
      ) {
        if (this.state[key] === '') {
          // Remove numeric attributes that have empty string
          // DynamoDB will NOT accept empty string....Stupid, right!
          // nulls will be given the value of true....weirdness!
          bloodwork[key] = undefined;
        } else {
          const nbr: number = Number(this.state[key]);
          if (isNaN(nbr)) {
            bloodwork[key] = this.state[key];
          } else {
            bloodwork[key] = nbr;
          }
        }
      }
    }
    // Practitioner-typed, per-test markers. Omitted entirely when there are
    // none, so the attribute never lands on the DynamoDB item as an empty array.
    const customMarkers = customMarkersFromRows(this.state.customMarkerRows);
    bloodwork.customMarkers = customMarkers.length ? customMarkers : undefined;

    // Carried straight through from the record being edited. Nothing on this
    // form can change them, so the only job here is not to lose them. Omitted
    // when empty, so the attribute never lands on the item as an empty array.
    const supplementEdits = Array.isArray(this.state.supplementEdits)
      ? this.state.supplementEdits
      : undefined;
    bloodwork.supplementEdits =
      supplementEdits && supplementEdits.length ? supplementEdits : undefined;

    // console.log(bloodwork);
    this.validate(bloodwork, validationErrors);
  };

  validate(bloodwork: Bloodwork, validationErrors: Map<string, string>) {
    // console.log(bloodwork);

    // Special check for bilirubin
    if (!bloodwork.isBilirubinValid()) {
      // Use a fake key here so the messages doesn't have the field name in it
      validationErrors.set(
        'fakeBilirubinKey1',
        'Bilirubin, Direct and Bilirubin, Indirect must not be greater than Bilirubin, Total.',
      );
      validationErrors.set(
        'fakeBilirubinKey2',
        'Click the Clear Bilirubin button next to Bilirubin, Total.',
      );
    }

    // Special check for collection date and client DOB
    if (bloodwork.collectionDate && bloodwork.clientId) {
      const { currentClients } = this.props;
      const client = Client.findClient(bloodwork.clientId, currentClients);

      if (!client.isBornOnOrBefore(bloodwork.collectionDate)) {
        validationErrors.set(
          'fakeCollectionDate',
          "Collection Date must be the same or after the client's Date of Birth.",
        );
      }
    }

    // Special check for MPV (Mean Platelet Volume) max value
    if (bloodwork.meanPlateletVolume && bloodwork.meanPlateletVolume > 20) {
      validationErrors.set(
        'meanPlateletVolume',
        'must not be greater than 20 fL.',
      );
    }

    // I³ section scores must be within 0..max for their section.
    for (const s of I3_SECTIONS) {
      const value = (bloodwork as any)[s.id];
      if (value === undefined || value === null || value === '') {
        continue;
      }
      if (value < 0 || value > s.max) {
        validationErrors.set(s.id, `must be between 0 and ${s.max}.`);
      }
    }

    // Practitioner-typed custom markers: name required and unique, lab min/max
    // required and ordered, every entered boundary numeric and non-decreasing.
    validateCustomMarkerRows(this.state.customMarkerRows).forEach(
      (message, key) => {
        validationErrors.set(key, message);
      },
    );

    // Special check to see if menstrualPhase is required and if we should clear it
    if (this.isMenstrualPhaseRequired() && !bloodwork.menstrualPhase) {
      validationErrors.set(
        'menstrualPhase',
        'is required for premenopausal female clients when a sex hormone marker (Estradiol, FSH, LH, Progesterone, etc.) is provided.',
      );
    } else if (!this.showMenstrualPhase()) {
      // If we arent supposed to show it then clear it
      bloodwork.menstrualPhase = undefined;
    }

    if (validationErrors.size > 0) {
      this.setState({
        validationErrors,
      });

      this.scrollToAddTestResults();
      return;
    }

    validate(bloodwork).then(errors => {
      // errors is an array of validation errors
      if (errors.length > 0) {
        for (let error of errors) {
          this.displayErrorMessage(error, validationErrors);
        }

        this.setState({
          validationErrors: validationErrors,
        });

        this.scrollToAddTestResults();
      } else {
        this.setState({
          validationErrors: new Map<string, string>(),
        });
        // Set WBC Differentials if not entered as percentages
        const wbcDifferentialUnits = parseInt(
          this.state.wbcDifferentialUnits,
          10,
        );
        bloodwork.convertWBCDifferentialsIfNecessary(wbcDifferentialUnits);
        // console.log('bloodwork:', bloodwork);
        this.saveBloodwork(bloodwork);
      }
    });
  }

  saveBloodwork(bloodwork: Bloodwork) {
    const {
      refreshBloodworks,
      loadClientBloodworks,
      bloodworkEntryStore,
      currentClients,
      updateClient,
    } = this.props;

    // Populate the embedded client snapshot for a new test. Clone the live
    // client so stamping height/weight/units onto the snapshot below does not
    // mutate the live record before the change-detection sync block runs (that
    // block compares against the live client to decide whether to persist it).
    if (bloodwork.isNew()) {
      const liveClient = Client.findClient(bloodwork.clientId, currentClients);
      bloodwork.client = liveClient ? new Client(liveClient) : new Client();
    }

    // The height/weight/units entered on a test are the client's measurements
    // as of that test, so the embedded snapshot must carry them. Every BMI /
    // Client-Information readout (analysis screen + PDF) reads bloodwork.client,
    // so without this the display reverts to the client's prior profile value
    // instead of what was just entered. Mirrors saveClientInfo in
    // bloodwork-test-analysis.tsx. Height is cm, weight is lbs (canonical).
    if (bloodwork.client) {
      bloodwork.client.height = bloodwork.height as number | undefined;
      bloodwork.client.weight = bloodwork.weight as number | undefined;
      bloodwork.client.units = this.state.bwUnits === 'SI' ? 'SI' : 'US';
    }

    bloodworkEntryStore
      .saveBloodwork(bloodwork)
      .then(savedBloodwork => {
        // console.log(savedBloodwork);
        this.addAlert(
          { type: 'success', text: 'Successfully saved test result!' },
          false,
          5000,
        );
        // Load the client bloodworks since we could be editing a bloodwork on the analyze bloodworks page
        if (loadClientBloodworks && bloodwork.clientId) {
          loadClientBloodworks(bloodwork.clientId);
        }
        refreshBloodworks();

        // Teach My Markers any custom marker this test introduced, so the next
        // client only needs it picked rather than defined again. Existing
        // library entries are left alone — My Markers is where ranges are
        // curated. Fire-and-forget: a failed profile write must not look like
        // a failed test save, since the test itself is already stored.
        const learned = learnDefinitionsFromMarkers(
          this.props.currentUserProfile?.customMarkerLibrary,
          bloodwork.customMarkers,
          () => uuidv4(),
        );
        if (learned && this.props.learnCustomMarkers) {
          this.props.learnCustomMarkers(learned).catch(err => {
            console.error('Failed to add markers to My Markers:', err);
            // The test is saved either way, so this is a warning rather than an
            // error — but it is not nothing: My Markers has silently stopped
            // learning, and if the cause is a full profile the practitioner
            // needs to hear it from the screen they were on, not discover it
            // the next time a profile save refuses. Kept alongside the save
            // confirmation rather than replacing it.
            this.addAlert(
              {
                type: 'warning',
                text: profileSaveErrorMessage(
                  err,
                  'This test is saved, but its new markers could not be added to My Markers. You can add them there by hand.',
                ),
              },
              true,
            );
          });
        }

        // Sync height/weight/units back to the Client record if they changed
        if (updateClient && bloodwork.clientId) {
          const client = Client.findClient(bloodwork.clientId, currentClients);
          if (client) {
            const newHeight = bloodwork.height as number | undefined;
            const newWeight = bloodwork.weight as number | undefined;
            const newUnits: 'US' | 'SI' = this.state.bwUnits === 'SI' ? 'SI' : 'US';
            // Treat undefined client.units as 'US' (the form default) to avoid spurious saves
            const effectiveClientUnits: 'US' | 'SI' = client.units === 'SI' ? 'SI' : 'US';
            if (client.height !== newHeight || client.weight !== newWeight || effectiveClientUnits !== newUnits) {
              client.height = newHeight;
              client.weight = newWeight;
              client.units = newUnits;
              updateClient(client).catch(err =>
                console.error('Failed to update client height/weight/units:', err),
              );
            }
          }
        }

        this.clearForm(false);
        this.handleNumberOfTestsLimit();
        bloodworkEntryStore.resetCurrentBloodwork();
        bloodworkEntryStore.setHideEnterTestResults(true);

        // If we arrived from Manage Test Results, return there with the
        // client preselected so the user lands back where they started.
        const history = this.props.history;
        const navState = history && history.location && history.location.state;
        if (navState && navState.returnTo && history) {
          history.push({
            pathname: navState.returnTo,
            state: { clientId: navState.clientId || bloodwork.clientId },
          });
        }
      })
      .catch(error => {
        console.error(error);
        this.addAlert({ type: 'danger', text: 'Failed to add test result.' });
      });
  }

  clearForm = (clearAlerts?: boolean) => {
    // Nothing is loaded into the form any more, so the next record that arrives
    // has to be, even if it is the one that was just saved.
    this.hydratedBloodworkId = undefined;
    this.clientResolvedBloodworkId = undefined;
    const isAddMode = this.isAddMode(this.props.history);
    const bloodwork = {};
    Object.keys(this.state).forEach(key => {
      if (
        key !== 'validationErrors' &&
        key !== 'collectionDate' &&
        key !== 'alerts' &&
        key !== 'selectedClient' &&
        key !== 'showUpgradeModal' &&
        key !== 'showCustomMarkersUpgradeModal' &&
        key !== 'showUploadPdfUpgradeModal' &&
        key !== 'showTimeSavingFeaturesModal'
      ) {
        bloodwork[key] = '';
      }
    });

    this.setState({
      ...bloodwork,
      panelTitle: this.state.panelTitle,
      validationErrors: new Map<string, string>(),
      collectionDate: undefined,
      collectionDateForUI: undefined,
      // When editing a client's test, keep the client selected — it's the
      // record being edited and the client selector is disabled in edit mode.
      selectedClient: isAddMode ? new Client() : this.state.selectedClient,
      clientId: isAddMode ? '' : this.state.clientId,
      wbcDifferentialUnits: WhiteBloodCellDifferentialUnitType.PERCENT,
      // The blanket `= ''` above would leave the custom-marker table with a
      // string where it expects an array.
      customMarkerRows: [emptyCustomMarkerRow()],
      customMarkerCarriedForwardFrom: undefined,
      sectionCardOrder: undefined,
      dirty: false,
    },
    // Clear empties the TEST, not the practitioner's setup. The default
    // sections belong to them rather than to any one test, so they come
    // straight back — clearing a mistyped panel otherwise cost them every
    // section they had configured, with no way back short of leaving the
    // screen and returning.
    //
    // In the CALLBACK, not after the call: loadDefaultCustomMarkers reads the
    // rows to decide whether anything has been typed, and setState is async, so
    // called inline it would still see the typed rows and refuse to reload.
    this.loadDefaultCustomMarkers);

    if (clearAlerts) {
      this.clearAlerts();
    }
  };

  clearAlerts = () => {
    this.setState({
      alerts: [],
    });
  };

  displayErrorMessage(
    error: ValidationError,
    validationErrors: Map<string, string>,
  ) {
    for (let constraint of Object.keys(error.constraints)) {
      validationErrors.set(error.property, error.constraints[constraint]);
    }
  }

  renderErrorListItems(validationErrors: Map<string, string>) {
    let listItems: JSX.Element[] = [];
    validationErrors.forEach((value: string, fieldName: string) => {
      if (validationErrors.get(fieldName)) {
        const fieldLabel = this.findFieldLabel(fieldName);
        listItems.push(
          <li key={fieldName + '-error'}>
            {fieldLabel} {validationErrors.get(fieldName)}
          </li>,
        );
      }
    });

    return listItems;
  }

  findFieldLabel(attribute: string): string {
    const panelField: PanelField = PanelField.ALL_FIELDS.find(field => {
      return field.id === attribute;
    });
    return panelField ? panelField.label : '';
  }

  renderValidationErrors(validationErrors: Map<string, string>) {
    return (
      <div className="panel">
        <div className="validation-errors">
          <ul>{this.renderErrorListItems(validationErrors)}</ul>
        </div>
      </div>
    );
  }

  renderAlerts() {
    // console.log('renderAlerts called....');
    const alerts = this.state.alerts.map((alert: any, index: number) => {
      return this.renderAlert(alert, index);
    });

    return <div>{alerts}</div>;
  }

  renderAlert(alert: any, index: number) {
    return (
      <Alert key={index} variant={alert.type} dismissible>
        {alert.text}
      </Alert>
    );
  }

  renderErrors() {
    const { validationErrors } = this.state;
    return validationErrors.size > 0 ? (
      this.renderValidationErrors(validationErrors)
    ) : (
      <div />
    );
  }

  addAlert = (
    alert: any,
    keepCurrentAlerts: boolean = false,
    autoClose?: number,
  ) => {
    // console.log('add alert called....');
    const alerts = keepCurrentAlerts ? [...this.state.alerts, alert] : [alert];
    this.setState({
      alerts,
    });

    if (autoClose) {
      setTimeout(() => {
        this.clearAlerts();
      }, autoClose);
    }
  };

  createFieldElements = (field: PanelField) => {
    if (field.id === 'collectionDate') {
      return this.collectionDate(field);
    }
    if (field.id === 'clientId') {
      return this.selectClient(field);
    }
    if (field.id === 'wbcDifferentialUnits') {
      return this.wbcDifferentialUnits(field);
    }
    if (field.id === 'menstrualPhase') {
      return this.menstrualPhase(field);
    }
    return this.input(field);
  };

  collectionDate(field: PanelField) {
    return (
      // TODO: Look into using a customInput here so that validation styling is applied.
      <DatePicker
        selected={this.state.collectionDateForUI}
        onChange={this.handleCollectionDateChange}
        maxDate={new Date()}
        peekNextMonth
        showMonthDropdown
        showYearDropdown
        todayButton
        dropdownMode="select"
        dateFormat="MM/dd/yyyy"
        placeholderText="MM/DD/YYYY"
        customInput={<Form.Control style={{ fontSize: '14px' }} />}
        popperPlacement="bottom"
        popperModifiers={{
          flip: {
            behavior: ['bottom'], // don't allow it to flip to be above
          },
          preventOverflow: {
            enabled: false, // tell it not to try to stay within the view (this prevents the popper from covering the element you clicked)
          },
          hide: {
            enabled: false, // turn off since needs preventOverflow to be enabled
          },
        }}
      />
    );
  }

  selectClient(field: PanelField) {
    console.log(
      'rendering selectClient......disableSelectClient: ' +
        this.state.disableSelectClient,
    );
    const { currentClients } = this.props;

    if (currentClients === undefined) {
      return <Spinner name="ball-clip-rotate" color="#337ab7" fadeIn="none" />;
    }

    if (currentClients.length === 0) {
      return (
        <LinkContainer exact to="/client/add">
          <Button>Add Client</Button>
        </LinkContainer>
      );
    }

    const sorted = currentClients
      .slice()
      .sort((a: Client, b: Client) =>
        (a.lastName || '').localeCompare(b.lastName || ''),
      );

    const options = sorted.map((c: Client) => ({
      value: c.id,
      label: `${c.lastName}, ${c.firstName}`,
    }));

    const selectedValue =
      options.find((o) => o.value === this.state[field.id]) ?? null;
    const hasError = !!this.state.validationErrors.get(field.id);

    return (
      <Select
        placeholder="Search by name..."
        isSearchable
        // The client cannot be changed on an existing test. Derive this from
        // props (isEditingExistingTest) instead of the disableSelectClient
        // state flag: that flag is only set in componentWillReceiveProps (which
        // can be skipped when editing opens from the Home page Edit button) and
        // is never reset to false, so it would also wrongly stay disabled if an
        // Add form is opened in the same component instance after an edit.
        isDisabled={this.isEditingExistingTest()}
        options={options}
        value={selectedValue}
        onChange={(opt: any) => {
          const rawValue: string = opt?.value ?? '';
          this.setState({ [field.id]: rawValue || undefined });
          this.handleClientChange(rawValue);
          this.clearAlerts();
        }}
        maxMenuHeight={500}
        components={{ Option: ClientOption }}
        styles={{
          control: (base: any, state: any) => ({
            ...base,
            borderColor: hasError
              ? '#dc3545'
              : state.isFocused
              ? '#80bdff'
              : '#ced4da',
            boxShadow: hasError
              ? '0 0 0 0.2rem rgba(220,53,69,.25)'
              : state.isFocused
              ? '0 0 0 0.2rem rgba(0,123,255,.25)'
              : 'none',
            '&:hover': { borderColor: hasError ? '#dc3545' : '#ced4da' },
            fontSize: '14px',
            minHeight: '38px',
          }),
          menu: (base: any) => ({
            ...base,
            zIndex: 9999,
          }),
          placeholder: (base: any) => ({
            ...base,
            color: '#6c757d',
            fontSize: '14px',
          }),
          singleValue: (base: any) => ({
            ...base,
            fontSize: '12px',
            fontWeight: 600,
          }),
        }}
      />
    );
  }

  wbcDifferentialUnits(field: PanelField) {
    let wbcDifferentialUnits = parseInt(this.state.wbcDifferentialUnits, 10);
    // Make sure we default a value if none present
    if (!wbcDifferentialUnits) {
      wbcDifferentialUnits = WhiteBloodCellDifferentialUnitType.ABSOLUTE_KCELLS;
    }
    return (
      <InputGroup>
        <FormCheck
          inline
          id="unit2"
          name={field.id}
          value={WhiteBloodCellDifferentialUnitType.ABSOLUTE_KCELLS}
          label={WhiteBloodCellDifferentialUnit.ABSOLUTE_KCELLS}
          type="radio"
          onChange={this.handleInputChange}
          checked={
            wbcDifferentialUnits ===
            WhiteBloodCellDifferentialUnitType.ABSOLUTE_KCELLS
          }
        ></FormCheck>
        &nbsp;&nbsp;&nbsp;
        <FormCheck
          inline
          id="unit3"
          name={field.id}
          value={WhiteBloodCellDifferentialUnitType.ABSOLUTE_CELLS}
          label={WhiteBloodCellDifferentialUnit.ABSOLUTE_CELLS}
          type="radio"
          onChange={this.handleInputChange}
          checked={
            wbcDifferentialUnits ===
            WhiteBloodCellDifferentialUnitType.ABSOLUTE_CELLS
          }
        ></FormCheck>
        &nbsp;&nbsp;&nbsp;
        <FormCheck
          id="unit1"
          name={field.id}
          value={WhiteBloodCellDifferentialUnitType.PERCENT}
          inline
          label={WhiteBloodCellDifferentialUnit.PERCENT}
          type="radio"
          onChange={this.handleInputChange}
          checked={
            wbcDifferentialUnits === WhiteBloodCellDifferentialUnitType.PERCENT
          }
        ></FormCheck>
      </InputGroup>
    );
  }

  menstrualPhase(field: PanelField) {
    return (
      <Form.Control
        as="select"
        name="menstrualPhase"
        value={this.state.menstrualPhase}
        onChange={this.handleInputChange}
        isInvalid={!!this.state.validationErrors.get(field.id)}
      >
        <option value="">{field.placeholder}</option>
        <option value={MenstrualPhase.FOLLICULAR}>
          {MenstrualPhase.FOLLICULAR}
        </option>
        <option value={MenstrualPhase.OVULATION}>
          {MenstrualPhase.OVULATION}
        </option>
        <option value={MenstrualPhase.LUTEAL}>{MenstrualPhase.LUTEAL}</option>
        <option value={MenstrualPhase.NONE}>
          {MenstrualPhase.NONE} (children's ranges)
        </option>
      </Form.Control>
    );
  }

  input(field: PanelField) {
    const { validationErrors } = this.state;

    return (
      <InputGroup>
        <Form.Control
          name={field.id}
          onChange={this.handleInputChange}
          onBlur={this.handleOnBlur}
          placeholder={this.placeholder(field)}
          type="text"
          value={this.state[field.id]}
          isInvalid={!!validationErrors.get(field.id)}
        />
        {this.units(field)}
      </InputGroup>
    );
  }

  units(field: PanelField) {
    let units = field.units;
    const wbcDifferentialUnits = parseInt(this.state.wbcDifferentialUnits, 10);
    if (field.isWBCDifferential) {
      units = field.wbcUnits(wbcDifferentialUnits);
    }
    return units ? (
      <InputGroup.Append>
        <InputGroup.Text>{units}</InputGroup.Text>
      </InputGroup.Append>
    ) : (
      ''
    );
  }

  placeholder(field: PanelField): string {
    if (field.isWBCDifferential) {
      const wbcDifferentialUnits = parseInt(
        this.state.wbcDifferentialUnits,
        10,
      );
      return field.wbcPlaceholder(wbcDifferentialUnits);
    } else if (field.placeholder) {
      return field.placeholder;
    } else {
      const { bloodworkRanges } = this.props;
      const { selectedClient, collectionDate, menstrualPhase } = this.state;
      const bloodwork = new Bloodwork({
        client: selectedClient,
        collectionDate: collectionDate || moment().startOf('day').toISOString(),
        menstrualPhase,
      });

      const range = BloodworkRange.findRange(
        bloodworkRanges,
        field.id,
        bloodwork,
      );

      if (range) {
        // console.log('placeholder.................................');
        // console.log(range);
        return range.placeholder(field.getPlaceholderPrecision());
      }
    }
    return undefined;
  }

  clearBilirubin() {
    this.setState({
      bilirubin: '',
      bilirubinDirect: '',
      bilirubinIndirect: '',
    });
  }

  addAdditionalInfo(field: PanelField) {
    if (field.id === 'bilirubin' || field.id === 'bilirubinDirect') {
      const tooltip = (
        <Tooltip id="bilirubin-tooltip">
          <div>
            Click this button to clear the values for Bilirubin, Total, Direct,
            and Indirect if a mistake was made in entering Bilirubin, Direct or
            Indirect.
          </div>
          <div>
            Re-enter the value for Bilirubin, Total first, then enter a value
            for either Direct or Indirect and the other will be automatically
            calculated.
          </div>
        </Tooltip>
      );
      return (
        <OverlayTrigger overlay={tooltip}>
          <Button
            type="button"
            onClick={() => {
              this.clearBilirubin();
            }}
          >
            Clear Bilirubin
          </Button>
        </OverlayTrigger>
      );
    }
    return null;
  }

  isMenstrualPhaseRequired() {
    // Any marker whose ranges depend on the menstrual phase requires one to be
    // selected — not just Estradiol. Check every phase-dependent marker so
    // saving FSH/LH/Progesterone/etc. without a phase is also blocked.
    const hasPhaseDependentMarker = PanelRange.MENSTRUAL_PHASE_ATTRIBUTES.some(
      attribute => {
        const value = this.state[attribute];
        return value !== undefined && value !== null && value !== '';
      },
    );
    return hasPhaseDependentMarker && this.showMenstrualPhase();
  }

  showMenstrualPhase() {
    const { selectedClient, collectionDate } = this.state;
    if (selectedClient && collectionDate) {
      const age = selectedClient.ageAsOf(collectionDate);
      return selectedClient.isPremenopausal() && age > 10;
    }
    return false;
  }

  renderMenstrualPhaseInfoIcon() {
    const tooltip = (
      <Tooltip id="menstrual-phase-info-tooltip">
        Click to view the phases of the menstrual cycle.
      </Tooltip>
    );
    return (
      <OverlayTrigger overlay={tooltip}>
        <FontAwesomeIcon
          icon="info-circle"
          style={{ marginLeft: '6px', cursor: 'pointer', color: '#337ab7' }}
          onClick={() => this.setState({ showMenstrualPhaseInfo: true })}
        />
      </OverlayTrigger>
    );
  }

  formGroup(field: PanelField) {
    const className = field.isFormCheck ? 'labsmarts-form-check-label' : '';
    // Only show menstrual phase if necessary
    if (field.id === 'menstrualPhase' && !this.showMenstrualPhase()) {
      return null;
    }
    return (
      <Form.Group
        as={Row}
        key={field.id + '-group'}
        controlId={field.id + '-group'}
      >
        <Form.Label column sm className={className}>
          {field.label}
          {field.id === 'menstrualPhase' &&
            devOnly() &&
            this.renderMenstrualPhaseInfoIcon()}
        </Form.Label>
        <Col sm>{this.createFieldElements(field)}</Col>
        <Col sm>{this.addAdditionalInfo(field)}</Col>
      </Form.Group>
    );
  }

  // A yes/no toggle for the I³ section, persisted as a 0/1 number so it flows
  // through the same save pipeline as the numeric fields.
  i3ToggleGroup(id: string, label: string) {
    const checked = !!Number(this.state[id]);
    return (
      <Form.Group as={Row} key={id + '-group'} controlId={id + '-group'}>
        <Form.Label column sm>
          {label}
        </Form.Label>
        <Col sm>
          <Form.Check
            type="checkbox"
            id={id}
            name={id}
            label="Yes"
            checked={checked}
            onChange={(e: any) =>
              this.setState({ [id]: e.target.checked ? 1 : 0 })
            }
          />
        </Col>
        <Col sm />
      </Form.Group>
    );
  }

  // Add Test Results card for the Immune & Inflammatory Burden Index (I³).
  // Env-gated to local dev only (hidden in slava + prod), matching the report sections.
  renderImmuneInflammatoryCard() {
    return (
      <Card>
        <Card.Header>Immune &amp; Inflammatory Burden Index (I³)</Card.Header>
        <Card.Body>
          <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '12px' }}>
            Enter each section's questionnaire subtotal (0 to the shown maximum).
            Weighted scores, the total Weighted I³ Score, and the Root Cause
            triggers are calculated automatically.
          </p>
          <Form noValidate>
            {PanelField.IMMUNE_INFLAMMATORY_FIELDS.map((field: any) =>
              field.isNotEditable ? null : this.formGroup(field),
            )}
            <hr />
            <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '12px' }}>
              Toxins and Trauma share the same questionnaire sections. These
              optional flags help separate them.
            </p>
            {this.i3ToggleGroup('i3ToxinExposure', 'Known toxin / mold exposure')}
            {this.i3ToggleGroup('i3TraumaHistory', 'Trauma / ACE / chronic-stress history')}
          </Form>
        </Card.Body>
      </Card>
    );
  }

  // Add Test Results card for Metabolic Vulnerability (MVX Plus). Parked behind
  // SHOW_MVX_CARD, and env-gated to local dev only (hidden in slava + prod) on
  // top of that, matching the report section.
  renderMvxCard() {
    return (
      <Card>
        <Card.Header>
          Metabolic Vulnerability Index (MVX) Plus
          <OverlayTrigger
            overlay={<Tooltip id="mvx-labcorp-tooltip">View the Labcorp MVX Plus test page (#123601)</Tooltip>}
          >
            <a
              href={LABCORP_MVX_PLUS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="external-link"
              style={{ marginLeft: '8px' }}
            >
              <FontAwesomeIcon icon="external-link-alt" />
            </a>
          </OverlayTrigger>
        </Card.Header>
        <Card.Body>
          <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '12px' }}>
            Enter the three index scores and six factor values from the Labcorp
            MVX Plus report. Zones, sub-indices, and patterns are calculated
            automatically.
          </p>
          <Form noValidate>
            {PanelField.MVX_FIELDS.map((field: any) =>
              field.isNotEditable ? null : this.formGroup(field),
            )}
          </Form>
        </Card.Body>
      </Card>
    );
  }

  scrollToAddTestResults() {
    const element = document.getElementById('add-test-results-panel');
    if (element) {
      element.scrollIntoView();
    }
  }

  showTimeSavingFeaturesModal = () => {
    this.setState({
      showTimeSavingFeaturesModal: true,
    });
  };

  async displayTimeSavingFeaturesModal(): Promise<boolean> {
    const { currentUserProfile } = this.props;
    if (currentUserProfile) {
      const hasSeenTimeSavingFeaturesModal = await localStorage.getItem(
        `${currentUserProfile.id}-hasSeenTimeSavingFeaturesModal`,
      );
      return hasSeenTimeSavingFeaturesModal !== 'true';
    } else {
      return Promise.resolve(false);
    }
  }

  hasSeenTimeSavingFeaturesModal = (): void => {
    // console.log('hasSeenTimeSavingFeaturesModal invoked....');
    const { currentUserProfile } = this.props;
    localStorage.setItem(
      `${currentUserProfile.id}-hasSeenTimeSavingFeaturesModal`,
      'true',
    );
    this.setState({
      showTimeSavingFeaturesModal: false,
    });
  };

  scrollTop() {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }

  // Lands the top of the Add Test Results panel just below the sticky header.
  // React Router (v5) preserves the previous page's scroll offset across a push
  // navigation, so arriving here from a long list (e.g. the Manage Clients
  // table, scrolled well down) inherits that offset and leaves the user partway
  // into the form, around the BMI row above CBC. A single synchronous reset on
  // mount doesn't stick because the browser re-applies the inherited offset and
  // async data finishes settling the layout afterward, so we re-run on the next
  // paint and once more after a short delay. Mirrors scrollToAnalyzeTestResults.
  scrollToAddTestResultsTop() {
    const run = () => {
      const element = document.getElementById('add-test-results-panel');
      if (!element) {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        return;
      }
      const sticky = document.querySelector('.App-sticky-top') as HTMLElement | null;
      const stickyHeight = sticky ? sticky.getBoundingClientRect().height : 0;
      const top = element.getBoundingClientRect().top + window.scrollY - stickyHeight;
      window.scrollTo({ top: Math.max(top, 0) });
    };
    run();
    requestAnimationFrame(run);
    setTimeout(run, 300);
  }

  toggleEnterTestResults() {
    this.setState({
      hideEnterTestResults: !this.state.hideEnterTestResults,
    });
  }

  render() {
    const {
      panelTitle,
      showUpgradeModal,
      showUploadPdfUpgradeModal,
      showTimeSavingFeaturesModal,
      hideEnterTestResults,
    } = this.state;
    const addTestResultsInfoTooltip = (
      <Tooltip id="modal-tooltip">
        Click this icon to learn more about time saving features.
      </Tooltip>
    );
    const hideEnterTestResultsIconName = hideEnterTestResults
      ? 'chevron-down'
      : 'chevron-up';

    return (
      <div>
        {/* In-app navigation. The menu, the client list and the Home page are
            all one click from here, and nothing on this form is autosaved. */}
        <Prompt
          when={this.state.dirty}
          message="This test has not been saved yet. Leave the page and the results you have entered will be lost."
        />
        {this.renderAlerts()}

        {/* The limit, stated where it cannot be missed and cannot be dismissed.
            A modal on mount was the only warning, and it is dismissible: the
            practitioner closed it, entered a whole panel including every custom
            marker, pressed Save, and got the same modal back with nothing
            saved. An hour of typing for a refusal that was knowable before the
            first field.

            Not an alert they can close, for the same reason: the answer to "can
            I save this" must be on screen the entire time it is being filled
            in. The Save buttons below are disabled to match, so the refusal is
            never a surprise sprung at the end. */}
        {this.blockedByTestLimit() ? (
          <Alert variant="warning">
            <strong>
              You have reached the maximum number of test results allowed.
            </strong>{' '}
            A new test cannot be saved until your plan is upgraded, so please
            upgrade before entering one. You can still open and edit the tests
            you have already saved.
            <div className="mt-2">
              <a
                href="https://labsmarts.com/pricing"
                target="_blank"
                rel="noopener noreferrer"
              >
                Free user - click here to upgrade.
              </a>
              <br />
              <a
                href="https://labsmarts.com/my-account"
                target="_blank"
                rel="noopener noreferrer"
              >
                Paid user - click here to upgrade
              </a>
            </div>
          </Alert>
        ) : null}

        <GenericModal
          show={showUpgradeModal}
          title="Maximum Number of Test Results Reached"
          text="You have reached the maximum number of test results allowed."
          link="https://labsmarts.com/pricing"
          linkText="Free user - click here to upgrade."
          additionalLink="https://labsmarts.com/my-account"
          additionalLinkText="Paid user - click here to upgrade"
          onHide={() => this.setState({ showUpgradeModal: false })}
        />

        <GenericModal
          show={this.state.showCustomMarkersUpgradeModal}
          title={CUSTOM_MARKER_UPGRADE_MODAL.title}
          text={CUSTOM_MARKER_UPGRADE_MODAL.text}
          link={CUSTOM_MARKER_UPGRADE_MODAL.link}
          linkText={CUSTOM_MARKER_UPGRADE_MODAL.linkText}
          additionalLink={CUSTOM_MARKER_UPGRADE_MODAL.additionalLink}
          additionalLinkText={CUSTOM_MARKER_UPGRADE_MODAL.additionalLinkText}
          onHide={() =>
            this.setState({ showCustomMarkersUpgradeModal: false })
          }
        />

        <GenericModal
          show={showUploadPdfUpgradeModal}
          title="Upload Test Results Feature Not Available"
          text="Upload Test Results feature is not available."
          link="https://labsmarts.com/upload_test_results_add_on"
          linkText="Click here to upgrade."
          onHide={() => this.setState({ showUploadPdfUpgradeModal: false })}
        />

        <TimeSavingFeaturesModal
          show={showTimeSavingFeaturesModal}
          onHide={this.hasSeenTimeSavingFeaturesModal}
        />

        <Modal
          show={this.state.showMenstrualPhaseInfo}
          onHide={() => this.setState({ showMenstrualPhaseInfo: false })}
          size="lg"
          centered
        >
          <Modal.Header
            closeButton
            style={{ backgroundColor: '#337ab7', color: '#fff' }}
          >
            <Modal.Title>Menstrual Cycle Phases</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            <img
              src={menstrualCycleDiagram}
              alt="Diagram of the female menstrual cycle showing the follicular and luteal phases, ovulation, and menstruation"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          </Modal.Body>
        </Modal>

        <Card id="add-test-results-panel">
          <Card.Header onClick={() => this.toggleEnterTestResults()}>
            <FontAwesomeIcon icon={hideEnterTestResultsIconName} />
            &nbsp;&nbsp;{panelTitle}
            <OverlayTrigger overlay={addTestResultsInfoTooltip}>
              <FontAwesomeIcon
                icon="info-circle"
                className="float-right"
                onClick={this.showTimeSavingFeaturesModal}
              />
            </OverlayTrigger>
          </Card.Header>

          {hideEnterTestResults ? null : (
            <Card.Body>
              {this.renderErrors()}
              <br />
              <Form noValidate>
                {PanelField.GENERAL_FIELDS.map((field: any, index: number) => {
                  return field.isNotEditable ? null : this.formGroup(field);
                })}
                {this.props.currentUserProfile?.canSeeUploadTestResults() && (
                  <Upload
                    isUploadAvailable={this.props.currentUserProfile?.canUploadPdf()}
                    showModal={() => this.handleUploadPdfLimit()}
                    setState={obj => {
                      this.setState(prev => ({ ...prev, ...obj }));
                      setTimeout(() =>
                        Object.keys(obj).forEach(key =>
                          this.handleOnBlur({ target: { name: key } }),
                        ),
                      );
                    }}
                    clearForm={() => this.clearForm(true)}
                    onReviewImport={result => {
                      // Values land like any other upload; the custom markers
                      // the read proposed are APPENDED to whatever rows the
                      // practitioner already has, never replacing them.
                      // The collection date is FREE TEXT on the review screen
                      // — the practitioner corrects what the read guessed —
                      // and moment() accepts anything. A date it cannot parse
                      // used to be stored as `null` with an "Invalid Date" in
                      // the picker beside it, which is a worse outcome than
                      // leaving the field alone for them to fill in.
                      const readDate = result.collectionDate
                        ? moment(result.collectionDate)
                        : undefined;
                      const dateFields =
                        readDate && readDate.isValid()
                          ? {
                              collectionDate: readDate
                                .clone()
                                .startOf('day')
                                .toISOString(),
                              collectionDateForUI: readDate.toDate(),
                            }
                          : {};
                      this.setState(
                        prev => {
                          // A second "Scan for more markers" over the same
                          // report proposes the same markers again. Appended
                          // blind they become duplicate rows, which the save
                          // validator then refuses ("this name is already used
                          // on row N") — so the practitioner is blocked by a
                          // button that looks like it worked.
                          const kept = (prev.customMarkerRows || []).filter(
                            row => !isBlankCustomMarkerRow(row),
                          );
                          const alreadyOnTest = rowKeysInUse(kept);
                          const added = rowsFromCustomMarkers(
                            result.customMarkers,
                          ).filter(row => {
                            const key = CustomMarker.keyFor(row.name);
                            if (!row.name.trim() || alreadyOnTest.has(key)) {
                              return false;
                            }
                            // Tracked as we go, so one read proposing the same
                            // name twice cannot add it twice either.
                            alreadyOnTest.add(key);
                            return true;
                          });
                          return {
                          ...prev,
                          ...result.values,
                          ...dateFields,
                          customMarkerRows: [
                            ...kept,
                            ...added,
                            emptyCustomMarkerRow(),
                          ],
                          };
                        },
                        () =>
                          Object.keys(result.values).forEach(key =>
                            this.handleOnBlur({ target: { name: key } }),
                          ),
                      );
                    }}
                  />
                )}
                <Form.Group as={Row} controlId="bwUnitsGroup">
                  <Form.Label column sm={4} className="labsmarts-form-check-label">Units</Form.Label>
                  <Col sm={5}>
                    <Form.Check
                      className="form-check-inline-first"
                      inline
                      id="bw-units-us"
                      name="bwUnits"
                      label="US"
                      type="radio"
                      checked={this.state.bwUnits !== 'SI'}
                      onChange={() => {
                        const cm = this.state.height;
                        const { ft, inches } = cm != null ? MathUtils.cmToFtIn(Number(cm)) : { ft: '', inches: '' };
                        this.setState({
                          bwUnits: 'US',
                          heightFt: cm != null ? String(ft) : '',
                          heightIn: cm != null ? String(inches) : '',
                        });
                      }}
                    />
                    <Form.Check
                      inline
                      id="bw-units-si"
                      name="bwUnits"
                      label="SI"
                      type="radio"
                      checked={this.state.bwUnits === 'SI'}
                      onChange={() => {
                        const ft = Number(this.state.heightFt) || 0;
                        const inches = Number(this.state.heightIn) || 0;
                        const cm = (this.state.heightFt || this.state.heightIn)
                          ? MathUtils.ftInToCm(ft, inches)
                          : this.state.height;
                        this.setState({ bwUnits: 'SI', height: cm });
                      }}
                    />
                    &nbsp;
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Tooltip id="bw-units-tooltip">
                          Currently only applies to height and weight.
                          <br /><br />
                          SI units for markers will be available in a future update.
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon
                        className="external-link"
                        icon="info-circle"
                        style={{ verticalAlign: 'middle', marginTop: '-2px', paddingBottom: '1px' }}
                      />
                    </OverlayTrigger>
                  </Col>
                </Form.Group>
                <Form.Group as={Row} controlId="heightEntryGroup">
                  <Form.Label column sm={4}>Height</Form.Label>
                  <Col sm={this.state.bwUnits === 'SI' ? 3 : 5}>
                    {this.state.bwUnits !== 'SI' ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div style={{ width: '78px' }}>
                          <Select
                            isSearchable={false}
                            maxMenuHeight={400}
                            onChange={(opt: any) => {
                              const ft = opt?.value ?? '';
                              const inches = Number(this.state.heightIn) || 0;
                              const cm = ft !== ''
                                ? MathUtils.ftInToCm(Number(ft), inches)
                                : (this.state.heightIn === '' ? undefined : this.state.height);
                              this.setState({ heightFt: ft, height: cm });
                            }}
                            options={[
                              { value: '', label: '—' },
                              ...[1,2,3,4,5,6,7,8].map(n => ({ value: String(n), label: String(n) })),
                            ]}
                            styles={{
                              control: (b: any) => ({ ...b, minHeight: '31px', height: '31px', borderColor: '#ced4da', boxShadow: 'none', fontSize: '12px' }),
                              valueContainer: (b: any) => ({ ...b, padding: '0 6px', height: '31px' }),
                              input: (b: any) => ({ ...b, margin: '0', padding: '0' }),
                              indicatorsContainer: (b: any) => ({ ...b, height: '31px' }),
                              dropdownIndicator: (b: any) => ({ ...b, padding: '4px' }),
                              menu: (b: any) => ({ ...b, zIndex: 9999 }),
                              option: (b: any) => ({ ...b, fontSize: '12px', padding: '4px 8px' }),
                              singleValue: (b: any) => ({ ...b, fontSize: '12px' }),
                            }}
                            value={{ value: this.state.heightFt ?? '', label: this.state.heightFt || '—' }}
                          />
                        </div>
                        <span className="input-group-text">ft</span>
                        <div style={{ width: '78px' }}>
                          <Select
                            isSearchable={false}
                            maxMenuHeight={400}
                            onChange={(opt: any) => {
                              const inches = opt?.value ?? '';
                              const ft = Number(this.state.heightFt) || 0;
                              const cm = inches !== ''
                                ? MathUtils.ftInToCm(ft, Number(inches))
                                : (this.state.heightFt === '' ? undefined : this.state.height);
                              this.setState({ heightIn: inches, height: cm });
                            }}
                            options={[
                              { value: '', label: '—' },
                              ...[0,1,2,3,4,5,6,7,8,9,10,11].map(n => ({ value: String(n), label: String(n) })),
                            ]}
                            styles={{
                              control: (b: any) => ({ ...b, minHeight: '31px', height: '31px', borderColor: '#ced4da', boxShadow: 'none', fontSize: '12px' }),
                              valueContainer: (b: any) => ({ ...b, padding: '0 6px', height: '31px' }),
                              input: (b: any) => ({ ...b, margin: '0', padding: '0' }),
                              indicatorsContainer: (b: any) => ({ ...b, height: '31px' }),
                              dropdownIndicator: (b: any) => ({ ...b, padding: '4px' }),
                              menu: (b: any) => ({ ...b, zIndex: 9999 }),
                              option: (b: any) => ({ ...b, fontSize: '12px', padding: '4px 8px' }),
                              singleValue: (b: any) => ({ ...b, fontSize: '12px' }),
                            }}
                            value={{ value: this.state.heightIn ?? '', label: this.state.heightIn !== '' && this.state.heightIn != null ? String(this.state.heightIn) : '—' }}
                          />
                        </div>
                        <span className="input-group-text">in</span>
                      </div>
                    ) : (
                      <InputGroup>
                        <Form.Control
                          maxLength={3}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const cm = e.target.value;
                            this.setState({ height: cm === '' ? undefined : Number(cm) });
                          }}
                          placeholder="e.g. 170"
                          size="sm"
                          style={{ maxWidth: '78px' }}
                          type="text"
                          value={this.state.height != null ? this.state.height : ''}
                        />
                        <InputGroup.Append>
                          <InputGroup.Text>cm</InputGroup.Text>
                        </InputGroup.Append>
                      </InputGroup>
                    )}
                  </Col>
                </Form.Group>
                <Form.Group as={Row} controlId="weightEntryGroup">
                  <Form.Label column sm={4}>Weight</Form.Label>
                  <Col sm={3}>
                    <InputGroup>
                      <Form.Control
                        size="sm"
                        maxLength={3}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const val = e.target.value;
                          if (val.trim() !== '' && Number(val) > 999) return;
                          if (this.state.bwUnits === 'SI') {
                            this.setState({ weight: val.trim() === '' ? '' : MathUtils.kgToLbs(Number(val)) });
                          } else {
                            this.setState({ weight: val });
                          }
                        }}
                        placeholder={this.state.bwUnits === 'SI' ? 'e.g. 68' : 'e.g. 150'}
                        style={{ maxWidth: '78px' }}
                        type="text"
                        value={
                          this.state.bwUnits === 'SI' && this.state.weight != null && this.state.weight !== ''
                            ? MathUtils.lbsToKg(Number(this.state.weight))
                            : (this.state.weight != null ? this.state.weight : '')
                        }
                      />
                      <InputGroup.Append>
                        <InputGroup.Text>{this.state.bwUnits === 'SI' ? 'kg' : 'lbs'}</InputGroup.Text>
                      </InputGroup.Append>
                    </InputGroup>
                  </Col>
                </Form.Group>
                {(() => {
                  const heightCm = this.state.height != null && this.state.height !== '' ? Number(this.state.height) : null;
                  const weightLbs = this.state.weight != null && this.state.weight !== '' ? Number(this.state.weight) : null;
                  if (heightCm == null || weightLbs == null) return null;
                  const bmi = MathUtils.bmi(weightLbs, heightCm);
                  return (
                    <Form.Group as={Row} controlId="bmiDisplayGroup">
                      <Form.Label column sm={4}>BMI</Form.Label>
                      <Col sm={3}>
                        <div style={{
                          border: '1px solid #ced4da',
                          borderRadius: '0.25rem',
                          padding: '0.25rem 0.5rem',
                          fontSize: '14px',
                          display: 'inline-block',
                          minWidth: '80px',
                          backgroundColor: '#fff',
                          lineHeight: '1.5',
                        }}>
                          {bmi}
                        </div>
                      </Col>
                    </Form.Group>
                  );
                })()}
              </Form>
              <Card>
                <Card.Header>
                  Complete Blood Count (CBC) w/ Differentials &amp; Platelets
                </Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.CBC_FIELDS.map((field: any, index: number) => {
                      return field.isNotEditable ? null : this.formGroup(field);
                    })}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Comprehensive Metabolic Panel (CMP)</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.CMP_FIELDS.map((field: any, index: number) => {
                      return field.isNotEditable ? null : this.formGroup(field);
                    })}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Lipid Panel</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.LIPID_FIELDS.map(
                      (field: any, index: number) => {
                        return field.isNotEditable
                          ? null
                          : this.formGroup(field);
                      },
                    )}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Iron Panel</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.IRON_FIELDS.map((field: any, index: number) => {
                      return field.isNotEditable ? null : this.formGroup(field);
                    })}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Thyroid Panel</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.THYROID_FIELDS.map(
                      (field: any, index: number) => {
                        return field.isNotEditable
                          ? null
                          : this.formGroup(field);
                      },
                    )}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Additional Markers</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.ADDITIONAL_FIELDS.map(
                      (field: any, index: number) => {
                        return field.isNotEditable
                          ? null
                          : this.formGroup(field);
                      },
                    )}
                  </Form>
                </Card.Body>
              </Card>
              <Card>
                <Card.Header>Hormones Panel</Card.Header>
                <Card.Body>
                  <Form noValidate>
                    {PanelField.SEX_HORMONE_FIELDS.map(
                      (field: any, index: number) => {
                        return field.isNotEditable
                          ? null
                          : this.formGroup(field);
                      },
                    )}
                  </Form>
                </Card.Body>
              </Card>
              {/* Practitioner-typed markers LabSmarts does not ship yet. Saved
                  with THIS test only — they get their own "Markers Added by the
                  User" section in the analysis and nothing else. */}
              {/* A card per section the practitioner named, then the
                  catch-all. A panel they run whole gets the weight the shipped
                  panels get: its own card with its own name, sitting among
                  them. Only THEIR sections, never a LabSmarts one — a second
                  card headed "Glucose Regulation & Metabolic Function" beside
                  the real glucose panel is two cards with one name. A marker
                  filed under a shipped section stays in the catch-all card
                  under its heading. */}
              {this.practitionerMarkerSections().map(group => (
                <Card key={group.key}>
                  <Card.Header>
                    {/* A flex row rather than a float: "Your section" was
                        floated right, which takes it out of the line box and
                        parks it against the top of the header instead of on
                        the same baseline as the name. */}
                    <div className="custom-marker-card-title">
                      {group.label}
                      <span className="custom-marker-card-count">
                        {group.count} marker
                        {group.count === 1 ? '' : 's'}
                      </span>
                      <span className="custom-marker-card-note">
                        Your section
                      </span>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <CustomMarkersTable
                      onlyGroupKey={group.key}
                      expandedSections={
                        this.props.currentUserProfile?.expandedTestSections
                      }
                      rows={this.state.customMarkerRows}
                      sections={
                        this.props.currentUserProfile?.customMarkerSections
                      }
                      clientGender={this.state.selectedClient?.gender}
                      carriedForwardFrom={
                        this.state.customMarkerCarriedForwardFrom
                      }
                      onCreateSection={this.createSection}
                      onRenameSection={this.renameSection}
                      customMarkersAllowed={this.props.currentUserProfile?.canUseCustomMarkers()}
                      onUpgradeNeeded={() =>
                        this.setState({ showCustomMarkersUpgradeModal: true })
                      }
                      onChange={(customMarkerRows: CustomMarkerRow[]) =>
                        this.setState({ customMarkerRows, dirty: true })
                      }
                    />
                  </Card.Body>
                </Card>
              ))}
              <Card>
                <Card.Header>
                  <CustomMarkersHeader />
                </Card.Header>
                <Card.Body>
                  <CustomMarkersTable
                    exceptGroupKeys={this.practitionerMarkerSections().map(
                      group => group.key,
                    )}
                    expandedSections={
                      this.props.currentUserProfile?.expandedTestSections
                    }
                    rows={this.state.customMarkerRows}
                    library={
                      this.props.currentUserProfile?.customMarkerLibrary
                    }
                    clientGender={this.state.selectedClient?.gender}
                    sections={
                      this.props.currentUserProfile?.customMarkerSections
                    }
                    onCreateSection={this.createSection}
                    onRenameSection={this.renameSection}
                    customMarkersAllowed={this.props.currentUserProfile?.canUseCustomMarkers()}
                    onUpgradeNeeded={() =>
                      this.setState({ showCustomMarkersUpgradeModal: true })
                    }
                    /* Handed over unconditionally: whether the banner is
                       warranted is decided by the table, off the rows THAT
                       CARD shows. Gated here it was keyed off every row on the
                       test, so the appendix card announced carried-forward
                       rows that had all moved up into the section cards. */
                    carriedForwardFrom={
                      this.state.customMarkerCarriedForwardFrom
                    }
                    onChange={(customMarkerRows: CustomMarkerRow[]) =>
                      this.setState({ customMarkerRows, dirty: true })
                    }
                  />
                </Card.Body>
              </Card>
              {SHOW_IMMUNE_INFLAMMATORY_CARD &&
                process.env.REACT_APP_ENV !== 'prod' &&
                process.env.REACT_APP_ENV !== 'slava' &&
                this.renderImmuneInflammatoryCard()}
              {SHOW_MVX_CARD &&
                process.env.REACT_APP_ENV !== 'prod' &&
                process.env.REACT_APP_ENV !== 'slava' &&
                this.renderMvxCard()}
              <Button
                id="submitButton"
                type="submit"
                onClick={this.handleSubmit}
                variant="primary"
                disabled={this.blockedByTestLimit()}
                title={
                  this.blockedByTestLimit()
                    ? 'You have reached the maximum number of test results allowed. Upgrade to save a new test.'
                    : undefined
                }
              >
                Save
              </Button>
              &nbsp;&nbsp;
              <Button
                id="clearButton"
                type="button"
                variant="outline-primary"
                onClick={() => this.clearForm(true)}
              >
                Clear
              </Button>
            </Card.Body>
          )}
        </Card>

        {hideEnterTestResults ? null : (
          <button
            onClick={this.handleSubmit}
            disabled={this.blockedByTestLimit()}
            title={
              this.blockedByTestLimit()
                ? 'You have reached the maximum number of test results allowed. Upgrade to save a new test.'
                : 'Save Test Results'
            }
            style={{
              position: 'fixed',
              bottom: '32px',
              right: '32px',
              height: '44px',
              padding: '0 20px',
              borderRadius: '22px',
              // Reads as unavailable rather than simply not working. The pill
              // is the Save most practitioners reach for, being the one that
              // does not need a scroll, so it has to carry the same answer the
              // banner at the top is giving.
              backgroundColor: this.blockedByTestLimit() ? '#adb5bd' : '#766d66',
              color: '#fff',
              border: 'none',
              cursor: this.blockedByTestLimit() ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              zIndex: 1050,
            }}
          >
            Save
          </button>
        )}
      </div>
    );
  }
}
